import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row } from 'reactstrap';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { InView } from 'react-intersection-observer';
import isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';

import { history } from 'Redux/store';
import Backtotopbutton from 'Components/Utility/Mobile/Backtotopbutton';
import Breadcrumbs from 'Components/Utility/SEO/Breadcrumbs';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import HitCard from 'Components/Cards/HitCard';
import messages from 'Components/FacetSearch/LanguagesStrings';
import Meta from 'Components/Utility/SEO/Meta';
import NotFound from 'Components/NotFound/NotFound';
import Paginate from 'Components/Utility/Paginate';
import PillFilters from 'Components/Utility/PillFilters';
import SkeletonHitCard from 'Components/Cards/SkeletonHitCard';
import SortByDropdown from 'Components/Utility/SortBy/SortByDropdown';
import TakeOver from 'Components/Banner/TakeOver';
import sortByMessages from 'Components/Utility/SortBy/SortByMessages';
import { fetchProductBySlug } from 'Redux/ducks/algolia-calls';


let displayTypes = [];

const sortShortcuts = {
  '#latest': 'release_date_desc',
  '#price': 'price_asc',
};

const StaticSearch = ({
  location: { search, hash },
  updateHits,
  title,
  fullTitle,
  formattedTitle,
  formattedFullTitle,
  desc,
  url,
  topBanner,
  seoBlurb,
  intl: { formatMessage },
  crumbs,
  showSortAndFilters,
  cardSizeLg,
  pageLimit,
}) => {
  const [hitsObject, setHits] = useState({ hits: null, nbHits: null, hitsPerPage: 72 });
  const [fetchError, setFetchError] = useState(null);
  const page = Number(getUrlParameter('page', search) || 1);
  const [filterType, setFilterType] = useState('');
  const initialSort = sortShortcuts[hash] || '';
  const [sort, setSort] = useState(initialSort);

  useEffect(() => {
    const fetchHits = async () => {
      const hits = await updateHits(page - 1, 36, filterType, sort);
      const sponsoredHit = await fetchProductBySlug('stellar-blade-complete-edition');

      if (url === 'latest-deals') {
        if (sponsoredHit && sponsoredHit.slug === 'stellar-blade-complete-edition') {
          const index = hits.hits.findIndex(hit => hit.slug === 'stellar-blade-complete-edition');

          if (index === -1) {
            // Scenario 1: Stellar Blade isn't in newHits so push it as the first item
            hits.hits.unshift(sponsoredHit);
          } else if (index > 0) {
            // Scenario 3: Stellar Blade is somewhere in newHits move it to the first position
            const [removedHit] = hits.hits.splice(index, 1);
            hits.hits.unshift(removedHit);
          }
          // Scenario 2: Stellar Blade is already the first item, do nothing
        }
        setHits(hits);
      }

      setHits(hits);
    };

    fetchHits().catch(setFetchError);
  }, [page, updateHits, filterType, sort, url]);

  if (fetchError) {
    throw fetchError;
  }

  const { hits, nbHits, hitsPerPage, facets } = hitsObject;

  const filterOptions = [
    { name: formatMessage(messages.all_word), type: '' },
    { name: formatMessage(messages.games), type: 'game' },
    { name: formatMessage(messages.dlc), type: 'dlc' },
  ];

  const sortByOptions = [
    { name: formatMessage(sortByMessages.topSellers), value: '' },
    { name: formatMessage(sortByMessages.priceLowHigh), value: 'price_asc' },
    { name: formatMessage(sortByMessages.priceHighLow), value: 'price_desc' },
    { name: formatMessage(sortByMessages.alphabetical), value: 'name' },
    { name: formatMessage(sortByMessages.topDiscount), value: 'discount' },
    { name: formatMessage(sortByMessages.latestDeals), value: 'latest_deals' },
    { name: formatMessage(sortByMessages.endingSoon), value: 'ending_soon' },
    { name: formatMessage(sortByMessages.mostWanted), value: 'most_wanted' },
    { name: formatMessage(sortByMessages.releaseDate), value: 'release_date_desc' },
    { name: formatMessage(sortByMessages.topRated), value: 'top_rated' },
  ];

  const onFilterChange = (type) => {
    setFilterType(type);
    if (page !== 1) {
      history.replace(`/${url}`);
    }
  };

  const onSortByChange = (sortValue) => {
    setSort(sortValue);
    if (page !== 1) {
      history.replace(`/${url}`);
    }
  };

  const seoTitle = `${formattedFullTitle || formatMessage(fullTitle)}${page > 1 ? ` | ${formatMessage(messages.page)} ${page}` : ''} | Fanatical`;
  const h1Title = `${formattedTitle || formatMessage(title)}${page > 1 ? `- ${formatMessage(messages.page)} ${page}` : ''}`;

  if (!filterType) displayTypes = Object.keys(_get(facets, 'display_type', {}));

  const showTypeFilters = displayTypes.includes('game') && displayTypes.includes('dlc');

  let formattedSeoBlurb = seoBlurb || '';

  if (seoBlurb && typeof seoBlurb === 'object') {
    formattedSeoBlurb = formatMessage(formattedSeoBlurb);
  }

  return (
    <React.Fragment>
      <Meta
        title={seoTitle}
        description={formattedSeoBlurb || formatMessage(desc)}
        url={url}
        hits={hits}
        nbHits={nbHits}
        hitsPerPage={hitsPerPage}
        pageType="CollectionPage"
      />
      {!isEmpty(crumbs) &&
        <Breadcrumbs crumbs={crumbs} />
      }
      {hits && !hits.length &&
        <NotFound />
      }
      {((hits && hits.length) || hits === null) &&
        <React.Fragment>
          {!!topBanner &&
            <TakeOver appearsOn={topBanner} />
          }
          <div className="container">
            <InView>
              {({ inView, ref }) => (
                <div ref={ref}>
                  <h1 className="pt-5 mb-4">{h1Title}</h1>
                  {!inView &&
                    <div className="d-block d-sm-none">
                      <Backtotopbutton />
                    </div>
                  }
                </div>
              )}
            </InView>

            {formattedSeoBlurb &&
              <p className="seo-blurb-text mb-4">{formattedSeoBlurb}</p>
            }

            {showSortAndFilters &&
              <div className={`categories-and-sortby-section ${showTypeFilters ? '' : 'justify-content-end'}`}>
                {showTypeFilters &&
                  <PillFilters
                    options={filterOptions}
                    value={filterType}
                    onChange={onFilterChange}
                  />
                }

                <SortByDropdown
                  options={sortByOptions}
                  value={sort}
                  onChange={onSortByChange}
                />
              </div>
            }

            <React.Fragment>
              {hits === null &&
                <div className="row">
                  <SkeletonHitCard
                    numberOfCards={72}
                    lgSize={cardSizeLg}
                  />
                </div>
              }

              {!isEmpty(hits) &&
                <ul className="HitCardsRow row">
                  {hits.map((hit, i) =>
                    <HitCard
                      key={hit.objectID}
                      hit={hit}
                      lgSize={cardSizeLg}
                      lazy={i >= 12}
                      hitCardContainerTag="li"
                      hitCardNameTag="h2"
                    />,
                  )}
                </ul>
              }
            </React.Fragment>

            <Row>
              <Paginate hits={hitsObject} pageLimit={pageLimit} />
            </Row>
          </div>
        </React.Fragment>
      }
    </React.Fragment>
  );
};

StaticSearch.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  updateHits: PropTypes.func.isRequired,
  title: PropTypes.shape({}).isRequired,
  fullTitle: PropTypes.shape({}).isRequired,
  formattedTitle: PropTypes.string,
  formattedFullTitle: PropTypes.string,
  desc: PropTypes.shape({}).isRequired,
  url: PropTypes.string.isRequired,
  topBanner: PropTypes.string,
  seoBlurb: PropTypes.shape({}),
  crumbs: PropTypes.arrayOf(PropTypes.object),
  showSortAndFilters: PropTypes.bool,
  cardSizeLg: PropTypes.number,
  pageLimit: PropTypes.number,
};

StaticSearch.defaultProps = {
  facet: 'publishers',
  topBanner: '',
  seoBlurb: null,
  formattedTitle: '',
  formattedFullTitle: '',
  showSortAndFilters: false,
  cardSizeLg: 3,
  pageLimit: 500,
};

const mapStateToProps = ({
  router: {
    location,
  },
}) => ({ location });

export default connect(mapStateToProps)(injectIntl(StaticSearch));
